import { Component, computed, ElementRef, HostListener, OnDestroy, OnInit, Renderer2, Signal, signal, WritableSignal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';
import { TraceMetadataType, TraceRouteData, TraceRouteType, TraceWayPointType } from '../../../models/trace-route-data';
import { BrowserSnifferService } from '../../../services/browser-sniffer.service';
import { MapService } from '../../../services/map.service';
import { PopupService } from '../../../services/popup.service';
import { ToggleEventNameType, ToggleEventService, ToggleEventType } from '../../../services/toggle-event.service';
import { ClickedPoint, TraceDrawerType, TraceRouteDataUpdateType, TraceService, } from '../../../services/trace.service';
import { PdfService } from '../../pdf/pdf.service';
import { TraceCreateConfirmationDialogComponent, TraceCreateConfirmationDialogData } from './trace-create-confirmation-dialog/trace-create-confirmation-dialog.component';
import { TraceCreateGeneralinfoDialogComponent, TraceCreateGeneralinfoUpdateData } from './trace-create-generalinfo-dialog/trace-create-generalinfo-dialog.component';
import { TraceCreateUndoService } from './trace-create-undo.service';
import { TraceCreateWaypointDialogComponent, TraceCreateWayPointUpdateData } from './trace-create-waypoint-dialog/trace-create-waypoint-dialog.component';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TraceImportComponent } from '../trace-import/trace-import.component';
import { TraceViewComponent } from '../trace-view/trace-view.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { BackEventService } from '@tt-lib/services/back-event.service';
import { MatDividerModule } from '@angular/material/divider';

@Component({
  selector: 'tt-trace-create',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatTooltipModule,
    TranslateModule,
    MatFormFieldModule,
    MatDividerModule
    // TraceImportComponent,
    // TraceCreateComponent,
    // TraceViewComponent,

  ],
  templateUrl: './trace-create.component.html',
  styleUrls: ['../trace.scss'],
  standalone: true
})
export class TraceCreateComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription;
  private _confirmationDialog: MatDialogRef<TraceCreateConfirmationDialogComponent, { data: TraceCreateConfirmationDialogData }>;
  private _updateDialog: MatDialogRef<TraceCreateWaypointDialogComponent, TraceCreateWayPointUpdateData>;
  private _generalinfoDialog: MatDialogRef<TraceCreateGeneralinfoDialogComponent, TraceCreateGeneralinfoUpdateData>;
  public editingExistingRoute: WritableSignal<boolean> = signal(false);
  public showAllIntermediateRoutePoints: WritableSignal<boolean> = signal(false);
  public isActive: Signal<boolean> = computed(() => this.traceService.isInEditingMode());

  public ref_intermediateRoutePointsCount: WritableSignal<number> = signal(0);
  public ref_formattedRouteLength: WritableSignal<string> = signal('0 <span>m</span>');
  public ref_formattedRouteUpwardDistance: WritableSignal<string> = signal('_');
  public ref_formattedRouteDownwardDistance: WritableSignal<string> = signal('_');
  public ref_hasWayPointsOnMap: WritableSignal<boolean> = signal(false);
  public ref_hasRouteOnMap: WritableSignal<boolean> = signal(false);
  public ref_isRouteWithGeneralInfo: WritableSignal<boolean> = signal(false);
  public ref_hasRouteToCreateProfile: WritableSignal<boolean> = signal(false);

  //public ref_traceRouteData: WritableSignal<TraceRouteData> = signal(null);
  public ref_traceRouteData_metadata: WritableSignal<TraceMetadataType> = signal(null);
  public ref_traceRouteData_waypoints: WritableSignal<TraceWayPointType[]> = signal(null);
  public ref_traceRouteData_routes: WritableSignal<TraceRouteType[]> = signal(null);

  private _lastTraceRouteDataUpdateType: WritableSignal<TraceRouteDataUpdateType> = signal(null);

  readonly maxNameLength = 100;
  readonly maxDescriptionLength = 1000;

  constructor(
    private _elRef: ElementRef,
    private _translateService: TranslateService,
    private _mapService: MapService,
    private _renderer: Renderer2,
    private _dialog: MatDialog,
    private _popupService: PopupService,
    private _toggleEventService: ToggleEventService,
    private _backEventService: BackEventService,
    public traceCreateUndoService: TraceCreateUndoService,
    public traceService: TraceService,
    public browsersniffer: BrowserSnifferService,
    public pdfService: PdfService
  ) {
    this._setReferencedInfo();

    this._confirmationDialog = null;
    this._updateDialog = null;
    this._generalinfoDialog = null;

    this._subscriptions = new Subscription();

    this._subscriptions.add(
      this.traceService.updatedTraceRouteData$.subscribe((traceRouteDataUpdateType: TraceRouteDataUpdateType) => {
        if (this.isActive()) {

          if ((this.traceCreateUndoService.isDataRefSetForUndoList() || this.traceService.hasPermalinkParam()) && !this.traceCreateUndoService.isSameDataRefAsUndoList(this.traceService.getPermalinkParam())) {
            //this will also clear the undo list
            this._checkForNewDataRef();
          }

          //COPY_TO_OTHER_TAB is not ignored, it normally added as the first (unremovable) item in the undo list
          //the clear all button from the drawing tools will trigger a REMOVED_DATA_MAP event, not REMOVED_DATA
          const isIgnoredTypeForUndoList = traceRouteDataUpdateType.indexOf('_temp') > -1
            || traceRouteDataUpdateType === TraceRouteDataUpdateType.UPDATED_ELEV_UPDOWN_DIST
            || traceRouteDataUpdateType === TraceRouteDataUpdateType.UNDO
            || traceRouteDataUpdateType === TraceRouteDataUpdateType.REMOVED_DATA;

          if (traceRouteDataUpdateType === TraceRouteDataUpdateType.COPY_TO_OTHER_TAB) {
            this.editingExistingRoute.set(this.traceService.hasTraceRouteOrWaypointOnMap() || this.traceService.hasTraceRouteWithGeneralInfo());
          }

          //add to undo list, unless ignored type
          if (!isIgnoredTypeForUndoList) {

            const autoRoutingDoneAfterRemovingRoutePoint = this._lastTraceRouteDataUpdateType() === TraceRouteDataUpdateType.MOVED_ROUTE_AFTER_REMOVAL_TEMP && traceRouteDataUpdateType === TraceRouteDataUpdateType.MOVED_ROUTE && this.traceCreateUndoService.getTopItemUndoList().type === TraceRouteDataUpdateType.REMOVED_ROUTEPOINT;

            if (autoRoutingDoneAfterRemovingRoutePoint) {
              //we don't put REMOVED_ROUTEPOINT + MOVED_ROUTE in the undo list, but we want the removed point with the new routing
              //we remove the temporary REMOVED_ROUTEPOINT again
              this.traceCreateUndoService.popUndoList();
              //and we put the MOVED_ROUTE as a REMOVED_ROUTEPOINT
              this.traceCreateUndoService.pushUndoList({ type: TraceRouteDataUpdateType.REMOVED_ROUTEPOINT, data: TraceRouteData.deepCopy(this.traceService.getTraceRouteData()) }, false);
              traceRouteDataUpdateType = TraceRouteDataUpdateType.REMOVED_ROUTEPOINT;
            } else {
              if (traceRouteDataUpdateType === TraceRouteDataUpdateType.MOVED_ROUTE_ERROR) {
                //we don't need to do anything for (_lastTraceRouteDataUpdateType() === MOVED_ROUTE_AFTER_REMOVAL_TEMP) 
                if (this._lastTraceRouteDataUpdateType() === TraceRouteDataUpdateType.ADDED_ROUTEPOINT_TEMP) {
                  traceRouteDataUpdateType = TraceRouteDataUpdateType.ADDED_ROUTEPOINT;
                } else if (this._lastTraceRouteDataUpdateType() === TraceRouteDataUpdateType.MOVED_ROUTE_TEMP) {
                  traceRouteDataUpdateType = TraceRouteDataUpdateType.MOVED_ROUTE;
                }
              }

              if (traceRouteDataUpdateType !== TraceRouteDataUpdateType.MOVED_ROUTE_ERROR) {
                //multiple times type TraceRouteDataUpdateType.COPY_TO_OTHER_TAB will be ignored by the undo service
                this.traceCreateUndoService.pushUndoList({ type: traceRouteDataUpdateType, data: TraceRouteData.deepCopy(this.traceService.getTraceRouteData()) }, traceRouteDataUpdateType === TraceRouteDataUpdateType.REDO);
              }
            }
          }

          if (traceRouteDataUpdateType.indexOf('_temp') === -1 && traceRouteDataUpdateType !== TraceRouteDataUpdateType.REMOVED_DATA) {
            this.traceService.saveTraceRouteDataToLocalStorage();
          }
        }

        if (this.isActive() || /*this.ref_traceRouteData()*/ this.ref_traceRouteData_routes() !== null) {
          this._setReferencedInfo();
        }

        //add extra tests if new types are added that have no influence on the route itself
        if (traceRouteDataUpdateType !== TraceRouteDataUpdateType.UPDATED_ELEV_UPDOWN_DIST) {
          this._lastTraceRouteDataUpdateType.set(this.isActive() ? traceRouteDataUpdateType : null);
        }
      })
    );

    this._subscriptions.add(this.traceService.delegatedElevationProfileRenewal$.pipe(debounceTime(1500)).subscribe((value: any) => {
      if (this.traceService.isInEditingMode()) {
        this.traceService.renewElevationProfileData(false);
      }
    }));

    this._subscriptions.add(
      this.traceService.clickedPoint$.subscribe((clickedPoint: ClickedPoint) => {
        if (this.isActive()) {
          const wasOpen = this.traceService.tabindex() === TraceDrawerType.CREATE_ROUTE && !this.traceService.isWatchingRouteWithoutDrawer();
          if (!wasOpen) {
            //if the import panel is open, this will switch the panel
            //if the drawer was closed completely, this will open it
            this.traceService.openTraceTab(TraceDrawerType.CREATE_ROUTE);
          }
          window.setTimeout(() => {
            this._scrollToInfo(clickedPoint);
          }, wasOpen ? 0 : 300);
        }
      })
    )

    this._subscriptions.add(
      this._popupService.customEvent.subscribe((eventObj) => {
        if (
          this.isActive() &&
          eventObj &&
          eventObj.detail &&
          eventObj.detail.eventId &&
          eventObj.detail.eventId === 'TraceInfoEdit' &&
          eventObj.detail.data &&
          typeof eventObj.detail.data.isPoi === 'boolean' &&
          Array.isArray(eventObj.detail.data.ref) &&
          ((eventObj.detail.data.isPoi && eventObj.detail.data.ref.length === 1) || (!eventObj.detail.data.isPoi && eventObj.detail.data.ref.length === 2))
        ) {

          const pointObj: TraceWayPointType = eventObj.detail.data.isPoi ? this.traceService.getTraceRouteData_wayPoint(eventObj.detail.data.ref[0]) : this.traceService.getTraceRouteData_routePoint(eventObj.detail.data.ref[0], eventObj.detail.data.ref[1]);

          if (pointObj) {

            if (this._popupService.isPopupAlreadyOnMap()) {
              this._popupService.hide();
            }

            if (eventObj.detail.data.isPoi) {
              this.editWayPoint(false, eventObj.detail.data.ref[0]);
            } else {
              this.deleteRoutePoint(eventObj.detail.data.ref[0], eventObj.detail.data.ref[1], true)
            }
          }
        }
      })
    );

    this._subscriptions.add(
      this._toggleEventService.emitter$.subscribe((toggleEvent: ToggleEventType) => {
        if (this.traceService.isActive() && (toggleEvent.forwardedFor === 'measure' || toggleEvent.forwardedFor === 'send-map-remark-panel' || toggleEvent.forwardedFor === 'send-email-remark')) {
          if (this.browsersniffer.isPhone() && this.traceService.isInEditingMode()) {
            this.traceService.setInEditingMode(false);
            this._toggleEventService.toggleEvent({
              forwardedFor: 'drawing-tools',
              eventName: ToggleEventNameType.CLOSE_COMMAND
            });
          }
        }
      })
    );
  }

  ngOnInit(): void {
    if (this.browsersniffer.isPhone()) {
      this.onOpenTraceCreate();
    }
    /*
    this._mapElement = this._mapService.getCurrentMap().getTargetElement();
    this._olControlElements = document.querySelectorAll<HTMLElement>(
      'tt-zoom, .elevation-profile-button-container, .baselayer-selector-button-container, .legend-button-container, .remarks-select-button-container, .trace-select-button-container, .measure-button-container, .ol-scale-line, tt-mouse-position,  tt-mouse-position-mapindex'
    );
    */
    // after resize from mobile phone dim
    if (this.isActive()) {
      this._setReferencedInfo();
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  /**
   * this handles wayPoint/routePoint element highlight
   * adding listener to every wayPoint/routePoint in html template (touchstart)='' creates many violation errors
   * @param event 
   */
  @HostListener('touchstart', ['$event'])
  //private _handleTouch(event: TouchEvent)
  protected handleTouch(event: TouchEvent) {
    let tag = <Element>event.target;
    while (!tag.classList.contains('wayPoint') && !tag.classList.contains('routePoint') && !tag.classList.contains('tab-viewport')) {
      tag = tag.parentElement;
    }
    if (tag.classList.contains('wayPoint')) {
      if (typeof tag.id === 'string' && tag.id.indexOf('_') > -1) {
        this.traceService.addWayPointHighlight(tag.id.indexOf('_startPoint') > -1 ? 'startPoint' : tag.id.indexOf('_endPoint') > -1 ? 'endPoint' : parseInt(tag.id.substring(tag.id.indexOf('_') + 1)), false);
      }
    } else if (tag.classList.contains('routePoint')) {
      if (typeof tag.id === 'string' && tag.id !== 'intermediateRoutePoints' && tag.id.indexOf('_') > -1 && tag.id.indexOf('_') !== tag.id.lastIndexOf('_')) {
        this.traceService.addRoutePointHighlight(parseInt(tag.id.substring(tag.id.indexOf('_') + 1, tag.id.lastIndexOf('_'))), parseInt(tag.id.substring(tag.id.lastIndexOf('_') + 1)), false);
      }
    }
  }

  @HostListener('window:keyup', ['$event'])
  //private _keyEvent(event: KeyboardEvent)
  protected keyEvent(event: KeyboardEvent) {
    if (this.isActive() && this.traceCreateUndoService.canUndoLastChange()) {
      let tag = <Element>event.target;
      if (event.ctrlKey && event.key === 'z' && tag && !tag.classList.contains('mat-input-element')) {
        this._closeAllOpenDialogs();
        this.traceCreateUndoService.undoLastChange();
      }
    }
  }

  onOpenTraceCreate() {
    if (!this.traceService.isInEditingMode()) {
      //this will also trigger this.isActive
      this.traceService.setInEditingMode(true);

      //the subscription to updatedTraceRouteData$ above wil call it also
      //this._checkForNewDataRef();

      if (this.traceService.checkForExternalMapEditingInteractions()) {
        this._toggleEventService.toggleEvent({
          forwardedFor: 'drawing-tools',
          eventName: ToggleEventNameType.INACTIVE_COMMAND
        });
      }

      //multilinestring will become linestring if necessary 
      let routesToOneRoute: TraceRouteType[] = [];

      if (this.traceService.getTraceRouteData().routes && this.traceService.getTraceRouteData().routes.length > 0) {

        const routesCopy: TraceRouteType[] = TraceRouteData.deepCopy(this.traceService.getTraceRouteData().routes);

        //if it was a gpx with track's (trk), it will now be considered as a gpx route (rte)
        //multilinestring features will be updated to linestring features by updating this value below
        routesCopy.forEach((route: TraceRouteType) => {
          route.wasTrack = false;
        });

        //we only support editing of 1 route
        //we convert multiple lines to one line, we connect the linestrings
        if (routesCopy.length > 1) {
          routesToOneRoute = [TraceRouteData.deepCopy(routesCopy[0])];

          for (let i = 1; i < routesCopy.length; i++) {
            const routeCopy: TraceRouteType = TraceRouteData.deepCopy(routesCopy[i]);

            for (let y = 0; y < routeCopy.routePoints.length; y++) {
              //check if the first point is not the same as the last from the previous linestring
              if (y == 0 && routesToOneRoute[0].routePoints.length > 0) {
                const lastWayPointPreviousLine = routesToOneRoute[0].routePoints[routesToOneRoute[0].routePoints.length - 1];
                if (TraceRouteData.matchingCoordinates([lastWayPointPreviousLine.x, lastWayPointPreviousLine.y], [routeCopy.routePoints[0].x, routeCopy.routePoints[0].y])) {
                  continue;
                }
              }
              routesToOneRoute[0].routePoints.push(routeCopy.routePoints[y])
            }
          }
        } else {
          routesToOneRoute = routesCopy;
        }
      }

      //gpx import with Z COORDINATE? currently we don't keep this coordinate in create mode (routes & waypoints)
      //if we would keep this coordinate in the future, then we don't need to create array wayPointsWithoutZ and we don't need to call this.traceService.setTraceRouteData_wayPoints below
      if (routesToOneRoute.length > 0) {
        //routesToOneRoute should now only have one array element after the check above
        for (let x = 0; x < routesToOneRoute[0].routePoints.length; x++) {
          if (typeof routesToOneRoute[0].routePoints[x].z === 'number') {
            delete routesToOneRoute[0].routePoints[x].z;
          }
        }
      }
      let wayPointsWithoutZ: TraceWayPointType[] = this.traceService.getTraceRouteData().wayPoints && this.traceService.getTraceRouteData().wayPoints.length > 0 ? TraceRouteData.deepCopy(this.traceService.getTraceRouteData().wayPoints) : [];
      for (let x = 0; x < wayPointsWithoutZ.length; x++) {
        if (typeof wayPointsWithoutZ[x].z === 'number') {
          delete wayPointsWithoutZ[x].z;
        }
      }

      /*
      TraceRouteDataUpdateType.COPY_TO_OTHER_TAB is used by the subscription above
      to set this component to active and initialize the undo list if necessary, the undo list needs one item (the view component will become inactive)
      */
      this.traceService.setTraceRouteData_routes(routesToOneRoute, TraceRouteDataUpdateType.COPY_TO_OTHER_TAB, false);//this should be with param true if we don't call this.traceService.setTraceRouteData_wayPoints
      this.traceService.setTraceRouteData_wayPoints(wayPointsWithoutZ, TraceRouteDataUpdateType.COPY_TO_OTHER_TAB);
    }

    //onclosetab/drawer??
    // this.editingExistingRoute.set(false);
    // this.traceService.setInEditingMode(false);
  }

  private _checkForNewDataRef() {
    const permalinkParam = this.traceService.getPermalinkParam();
    const isSameDataRefAsUndoList = this.traceCreateUndoService.isDataRefSetForUndoList() && this.traceCreateUndoService.isSameDataRefAsUndoList(permalinkParam);
    const setNewDataRef = !isSameDataRefAsUndoList && typeof permalinkParam === 'string';
    if (!isSameDataRefAsUndoList) {
      this.traceCreateUndoService.clearUndoList();
      this.editingExistingRoute.set(this.traceService.hasTraceRouteOrWaypointOnMap() || this.traceService.hasTraceRouteWithGeneralInfo());
    }
    if (setNewDataRef) {
      //needs to come after clearUndoList
      this.traceCreateUndoService.updateDataRefForUndoList(permalinkParam);
    }
  }

  private _setReferencedInfo() {
    if (this.isActive()) {
      this.ref_intermediateRoutePointsCount.set(this._countIntermediateRoutePoints());
      this.ref_hasWayPointsOnMap.set(this.traceService.hasWayPointsOnMap());
      this.ref_hasRouteOnMap.set(this.traceService.hasRouteOnMap());
      this.ref_isRouteWithGeneralInfo.set(this.traceService.hasTraceRouteWithGeneralInfo());
      this.ref_hasRouteToCreateProfile.set(this.traceService.hasRouteToCreateProfile());
      //this.ref_traceRouteData.set(this.traceService.getTraceRouteData());
      this.ref_traceRouteData_metadata.set(this.traceService.getTraceRouteData().metadata);
      const waypointsForHtmlView = this.traceService.getTraceRouteData().wayPoints.slice(0);
      waypointsForHtmlView.forEach((waypoint) => {
        waypoint.formattedName = this.formatWayPointNameForHtmlView(waypoint.name, true);
        waypoint.formattedDesc = this.formatWayPointDescriptionForHtmlView(waypoint.desc, false);
      });
      this.ref_traceRouteData_waypoints.set(waypointsForHtmlView);
      const routesForHtmlView = this.traceService.getTraceRouteData().routes.slice(0);
      // if we use name/decs for multiple routes in the future:
      // routesForHtmlView.forEach((route)=>{
      //  route.formattedName = this.formatWayPointNameForHtmlView(route.name, true);
      //  route.formattedDesc = this.formatWayPointDescriptionForHtmlView(route.desc, false);
      // });
      this.ref_traceRouteData_routes.set(routesForHtmlView);
    } else {
      this.ref_intermediateRoutePointsCount.set(0);
      this.ref_hasWayPointsOnMap.set(false);
      this.ref_hasRouteOnMap.set(false);
      this.ref_isRouteWithGeneralInfo.set(false);
      this.ref_hasRouteToCreateProfile.set(false);
      //this.ref_traceRouteData.set(TraceRouteData.getEmptyTraceRouteDataObject(this._mapService.getCurrentMap().getView().getProjection().getCode()));
      const emtyDataObject = TraceRouteData.getEmptyTraceRouteDataObject(this._mapService.getCurrentMap().getView().getProjection().getCode());
      this.ref_traceRouteData_metadata.set(emtyDataObject.metadata);
      this.ref_traceRouteData_waypoints.set(emtyDataObject.wayPoints);
      this.ref_traceRouteData_routes.set(emtyDataObject.routes);
    }

    this.ref_formattedRouteUpwardDistance.set(this.traceService.getElevationUpwardDist() > -1 ? TraceRouteData.formatRouteLength(this.traceService.getElevationUpwardDist(), true, '0 <span>m</span>') : '_');
    this.ref_formattedRouteDownwardDistance.set(this.traceService.getElevationDownwardDist() < 1 ? '-&nbsp;' + TraceRouteData.formatRouteLength(Math.abs(this.traceService.getElevationDownwardDist()), true, '0 <span>m</span>') : '_');

    this.ref_formattedRouteLength.set(this.isActive() ? TraceRouteData.formatRouteLength(this.traceService.getRouteLength(), true, '0 <span>m</span>') : '0 <span>m</span>');
  }


  toggleShowAllIntermediateRoutePoints() {
    this.showAllIntermediateRoutePoints.update((value) => !value);
  }

  private _countIntermediateRoutePoints() {
    //start and end don't count
    //autoDraw is not added
    //normally 1 route during edit mode
    let count = -2;
    if (this.traceService.getTraceRouteData().routes.length > 0) {
      this.traceService.getTraceRouteData().routes[0].routePoints.forEach((routePoint: TraceWayPointType) => {
        if (!routePoint.autoDraw) {
          count++;
        }
      });
    }
    return count > 0 ? count : 0;
  }

  getMetadataPropertyForInput(getName: boolean) {
    //return this.traceService.isActive() ? TraceRouteData.convertHtmlToInputValue(getName ? this.traceService.getTraceRouteData().metadata.name : this.traceService.getTraceRouteData().metadata.desc, !getName) : '';
    return this.traceService.isActive() ? TraceRouteData.getFilteredHtmlString(getName ? this.traceService.getTraceRouteData().metadata.name : this.traceService.getTraceRouteData().metadata.desc, getName, false) : '';
  }

  editWayPoint(isNew: boolean, index: number) {
    this._closeAllOpenDialogs();

    if (typeof index === 'number' && index >= 0 && this.traceService.getTraceRouteData().wayPoints.length > index) {
      this._updateDialog = this._dialog.open(TraceCreateWaypointDialogComponent, {
        panelClass: 'medium-dialog',
        data: {
          maxNameLength: this.maxNameLength,
          maxDescriptionLength: this.maxDescriptionLength,
          isNew: isNew,
          //name: isNew ? '' : TraceRouteData.convertHtmlToInputValue(this.traceService.getTraceRouteData().wayPoints[index].name, false),
          //desc: isNew ? '' : TraceRouteData.convertHtmlToInputValue(this.traceService.getTraceRouteData().wayPoints[index].desc, true),
          name: isNew ? '' : TraceRouteData.getFilteredHtmlString(this.traceService.getTraceRouteData().wayPoints[index].name, true, false),
          desc: isNew ? '' : TraceRouteData.getFilteredHtmlString(this.traceService.getTraceRouteData().wayPoints[index].desc, false, false),
          index: index
        }
      });
    }

    if (this._updateDialog !== null) {
      this._updateDialog.afterClosed().pipe(take(1)).subscribe(result => {
        if (result && typeof result.remove === 'boolean') {
          if (result.remove) {
            this.deleteWayPoint(result.index);
          } else {
            this.traceService.updateTraceRouteData_wayPoint(result.index, TraceRouteData.getFilteredHtmlString(result.name, true, false), TraceRouteData.getFilteredHtmlString(result.desc, false, true));
          }
        }
        this._updateDialog = null;
      });

      this._backEventService.registerOpenedWindow({ windowName: 'TraceCreateWaypointDialogComponent', isAutoCloseableWindow: true });
    }
  }

  editGeneralinfo(isNew: boolean) {
    this._closeAllOpenDialogs();
    this._generalinfoDialog = this._dialog.open(TraceCreateGeneralinfoDialogComponent, {
      panelClass: 'medium-dialog',
      data: {
        maxNameLength: this.maxNameLength,
        maxDescriptionLength: this.maxDescriptionLength,
        isNew: isNew,
        //name: TraceRouteData.convertHtmlToInputValue(this.traceService.getTraceRouteData().metadata.name, false),
        //desc: TraceRouteData.convertHtmlToInputValue(this.traceService.getTraceRouteData().metadata.desc, true),
        name: TraceRouteData.getFilteredHtmlString(this.traceService.getTraceRouteData().metadata.name, true, false),
        desc: TraceRouteData.getFilteredHtmlString(this.traceService.getTraceRouteData().metadata.desc, false, false),
      }
    });
    if (this._generalinfoDialog !== null) {
      this._generalinfoDialog.afterClosed().pipe(take(1)).subscribe(result => {
        if (result) {
          const metadata: TraceMetadataType = { time: this.traceService.isActive() ? this.traceService.getTraceRouteData().metadata.time : new Date().getTime() };
          const name = TraceRouteData.getFilteredHtmlString(result.name, true, false);
          if (name) {
            metadata.name = name;
          }
          const desc = TraceRouteData.getFilteredHtmlString(result.desc, false, true);
          if (desc) {
            metadata.desc = desc;
          }
          this.traceService.setTraceRouteData_metadata(metadata, false, true);
        }
        this._generalinfoDialog = null;
      });

      this._backEventService.registerOpenedWindow({ windowName: 'TraceCreateGeneralinfoDialogComponent', isAutoCloseableWindow: true });
    }
  }

  deleteGeneralinfo() {
    //with confirmation?
    //this.traceService.removeTraceRouteData_routePoint(routeIndex, routePointIndex);
    // const routePoint = this.traceService.getRoutePoint(routeIndex, routePointIndex);
    const data: TraceCreateConfirmationDialogData = {
      title: 'Name and description',
      text: this._translateService.instant('trace.create-route.confirmdelete'),
      type: 'metadata',
    };

    // if (wasMapClick) {
    //   data['continueLabel'] = this._translateService.instant('trace.create-route.delete');
    // }

    this._confirmDeletion(data);
  }

  deleteWayPoint(wayPointIndex: number) {
    //without confirmation?
    //this.traceService.removeTraceRouteData_wayPoint(wayPointIndex);

    this._confirmDeletion({
      title: this._translateService.instant('trace.poi') + (wayPointIndex + 1),
      text: this._translateService.instant('trace.create-route.confirmdelete'),
      type: 'wayPoint',
      index: [wayPointIndex]
    });
  }

  deleteRoutePoint(routeIndex: number, routePointIndex: number, wasMapClick: boolean) {
    //with confirmation?
    //this.traceService.removeTraceRouteData_routePoint(routeIndex, routePointIndex);
    const routePoint = this.traceService.getRoutePoint(routeIndex, routePointIndex);
    const data: TraceCreateConfirmationDialogData = {
      title: this._translateService.instant('trace.create-route.routepoint'),
      text: this._translateService.instant('trace.create-route.confirmdelete-routePoint') + ' ' + (routePoint ? '&lsquo;' + routePoint.formattedXY + '&rsquo;' : '') + (wasMapClick ? '' : '') + '?',
      type: 'routePoint',
      index: [routeIndex, routePointIndex]
    };

    if (wasMapClick) {
      data['continueLabel'] = this._translateService.instant('trace.create-route.delete');
    }

    this._confirmDeletion(data);
  }

  private _confirmDeletion(data: TraceCreateConfirmationDialogData) {
    /*
    if (this._confirmationDialog !== null) {
      this._confirmationDialog.close();
      this._confirmationDialog = null;
    }

    this._confirmationDialog = this._dialog.open(TraceCreateConfirmationDialogComponent, {
      panelClass: 'small-dialog',
      data: data,
    });

    this._backEventService.registerOpenedWindow({windowName: 'TraceCreateConfirmationDialogComponent', isAutoCloseableWindow: true});

    this._confirmationDialog.afterClosed().pipe(take(1)).subscribe(result => {
      if (result && result.data) {
        switch (result.data.type) {   
          case 'wayPoint': {
            this.traceService.removeTraceRouteData_wayPoint(result.data.index[0]);
            break;
          }
          case 'routePoint': {
            this.traceService.removeTraceRouteData_routePoint(result.data.index[0], result.data.index[1]);
            break;
          }
          case 'metadata': {
            this.traceService.removeTraceRouteData_metadata(true);
            break;
          }
          default: {
            //no action
          }
        }

        //if ('metadata' !== result.data.type) {
        this.traceService.removeWayPointHighlight();
        this.traceService.removeRoutePointHighlight();
        //}

     /*   
      }

      this._confirmationDialog = null;
    });
    */

    switch (data.type) {
      case 'wayPoint': {
        this.traceService.removeTraceRouteData_wayPoint(data.index[0]);
        this.traceService.removeWayPointHighlight();
        break;
      }
      case 'routePoint': {
        this.traceService.removeTraceRouteData_routePoint(data.index[0], data.index[1]);
        this.traceService.removeRoutePointHighlight();
        break;
      }
      case 'metadata': {
        this.traceService.removeTraceRouteData_metadata(true);
        this.traceService.removeGeneralInfoHighlight();
        break;
      }
      default: {
        //no action
      }
    }
  }

  doneDrawing() {
    //traceService.openTraceTab will trigger method switchTabType in trace.component.ts, and that will trigger method traceService.setInEditingMode(false)
    this.traceService.openTraceTab(TraceDrawerType.VIEW_ROUTE);
  }


  startNewDrawing() {
    // this.openTraceTab_str('CREATE_ROUTE');
    if (this._confirmationDialog !== null) {
      this._confirmationDialog.close();
      this._confirmationDialog = null;
    }

    this._confirmationDialog = this._dialog.open(TraceCreateConfirmationDialogComponent, {
      panelClass: 'small-dialog',
      data: {
        title: this._translateService.instant('trace.route_new_drawing'),
        text: this._translateService.instant('trace.create-route.confirmstart-new-route'),
        type: 'resetData'
      },
    });


    this._confirmationDialog.afterClosed().pipe(take(1)).subscribe(result => {
      if (result && result.data && result.data.type === 'resetData') {

        this.traceService.setPermalinkParam(false);
        this.traceService.fullCleanupAndSaveForHistoryItems();
        this.traceService.addNewHistoryItemForEditing();
        this.traceService.setPermalinkParam(true);

        this.traceService.resetTraceRouteData(true);

        this._setReferencedInfo();

        //the subscription to updatedTraceRouteData$ above wil call it also
        //this._checkForNewDataRef()
      }
      this._confirmationDialog = null;
    });

    this._backEventService.registerOpenedWindow({ windowName: 'TraceCreateConfirmationDialogComponent', isAutoCloseableWindow: true });

  }

  private _scrollToInfo(clickedPoint: ClickedPoint) {
    //find better solutions to scroll to element ? but the container has different types of elements, not only wayPoint containers
    let wayPointEl = this._elRef.nativeElement.querySelector('#' + clickedPoint.ref);
    const scrollContainer = document.querySelector('.trace-data');
    const scrollPosition = wayPointEl?.getBoundingClientRect().top + scrollContainer.scrollTop - scrollContainer.getBoundingClientRect().top

    if (!wayPointEl && !this.showAllIntermediateRoutePoints()) {
      //wayPointEl = this._elRef.nativeElement.querySelector('#intermediateRoutePoints');
      this.showAllIntermediateRoutePoints.set(true);
      window.setTimeout(() => {
        this._scrollToInfo(clickedPoint);
      }, 500);
      return;
    }

    if (wayPointEl) {
      if (this.browsersniffer.isPhone()) {
        wayPointEl.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'center',
        });
      } else {
        scrollContainer.scrollTo({
          top: scrollPosition,
          behavior: 'smooth',
        });
      }

      if (!clickedPoint.isNew) {
        this._renderer.addClass(wayPointEl, 'clicked');

        window.setTimeout(() => {
          this._renderer.removeClass(wayPointEl, 'clicked');
        }, 1000);
      }
    }
  }

  private _closeAllOpenDialogs() {
    if (this._confirmationDialog !== null) {
      this._confirmationDialog.close();
      this._confirmationDialog = null;
    }

    if (this._updateDialog !== null) {
      this._updateDialog.close();
      this._updateDialog = null;
    }
  }

  formatWayPointNameForHtmlView(name: string, placeholder: boolean) {
    return typeof name === 'string' && name.length > 0 ? name : placeholder ? '<div class=\'untitled\'>' + this._translateService.instant('trace.field.noname') + '</div>' : '';
  }
  formatWayPointDescriptionForHtmlView(desc: string, placeholder: boolean) {
    return typeof desc === 'string' && desc.length > 0 ? desc : placeholder ? this._translateService.instant('trace.field.nodescription') : '';
  }

}